import { Component, computed, effect, ElementRef, inject, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, NavigationEnd, NavigationStart, Router, RouterLink, RouterOutlet } from '@angular/router';
import { MainMenuComponent } from './components/main-menu/main-menu.component';
import { KeycloakEventType, KeycloakService } from 'keycloak-angular';
import { UserService } from './services/user.service';
import { filter, Subscription } from 'rxjs';
import { NgClass } from '@angular/common';
import { MatProgressBar } from '@angular/material/progress-bar';
import { LoadingService } from './services/loading.service';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import { MatDialog } from '@angular/material/dialog';
import { FeedbackComponent } from './components/feedback/feedback.component';
import { MiscService } from './services/misc.service';
import { environment } from '../environments/environment';
import { HeaderSignalService } from './services/header-signal.service';
import { GoogleAnalyticsService } from './services/google-analytics.service';
import { AccessibilityToolbarComponent } from './components/accessibility-toolbar/accessibility-toolbar.component';

@Component({
    selector: 'mobilize-root',
	imports: [RouterOutlet, MainMenuComponent, NgClass, MatProgressBar, RouterLink, MatProgressSpinner, AccessibilityToolbarComponent],
    templateUrl: './app.component.html',
    styleUrl: './app.component.scss'
})
export class AppComponent implements OnInit, OnDestroy {

    private _user = inject(UserService);
    private _kc = inject(KeycloakService);
    private _misc = inject(MiscService);
    private _ga = inject(GoogleAnalyticsService);
    private _router = inject(Router);
    private _route = inject(ActivatedRoute);
    private _loading = inject(LoadingService);
    private _dialog = inject(MatDialog);
    private _elementRef = inject(ElementRef);
    private _headerSignalService = inject(HeaderSignalService); // Inject the service

    superAccount = true;
    impersonatedAccount = false;
    registerPage = false;
    dataLoading = computed(() => this._loading.getLoadingState());
    title = 'mobilize-fs';
    userLogged = false;
    backOffice = false;
    showMenu = false;
    impersonatedPartner: string = '';
    processing = false;
    menuOpen = false;

    private subscription: Subscription = new Subscription();

    constructor() {
        effect(() => {
            const eventData = this._headerSignalService.headerEvent();
            this.menuOpen = eventData === 'menuOpen';
        });
        this._router.events.subscribe(event => {
            if (event instanceof NavigationStart) {
                this._headerSignalService.closeMenu();
            }
        });

    }

    ngOnInit() {
        this.loadCookieConsentScript();
        this._elementRef.nativeElement.removeAttribute("ng-version");
        this._router.events
            .pipe(
                filter(event => event instanceof NavigationEnd)
            )
            .subscribe((evt: NavigationEnd) => {
                const route = this.getChild(this._route);
                this._ga.pageView(evt.urlAfterRedirects);

                route.data.subscribe(data => {
                    this.registerPage = data['registerPage'];
                    this.backOffice = data['backoffice'];
                    this.showMenu = true;
                });
            });

        this._kc.getToken().then(token => {
            if (token) {
                this.userLogged = true;
                this._user.superStatus$.subscribe(rsp => {
                    this.superAccount = rsp;
                });
                this.subscription = this._user.impersonatedStatus$.subscribe(rsp => {
                    if (rsp && rsp.active) {
                        this.backOffice = false;
                        this.impersonatedAccount = rsp.active;
                        this.impersonatedPartner = rsp.name
                    }
                });
            }
        });

        // this._socket.initConnection();

        this._kc.keycloakEvents$.subscribe({
            next: (event) => {
                switch (event.type) {
                    case KeycloakEventType.OnAuthSuccess:
                        break;

                    case KeycloakEventType.OnTokenExpired:
                        this._kc.updateToken(2).then(rsp => {
                            this._kc.getToken().then(token => {
                                this._misc.emitTokenUpdate(token);
                            });
                        });
                        break;
                }
            }
        });
    }

    private loadCookieConsentScript(): void {
        const script = document.createElement('script');
        script.src = 'https://cdn.cookielaw.org/scripttemplates/otSDKStub.js';
        script.type = 'text/javascript';
        script.charset = 'UTF-8';
        script.setAttribute('data-domain-script', environment.cookieScriptDomain);
        script.setAttribute('data-document-language', 'true');

        // Add the script to the top of the <head>
        const head = document.head;
        if (head.firstChild) {
            head.insertBefore(script, head.firstChild);
        } else {
            head.appendChild(script); // Fallback in case the head has no children
        }
    }



    getChild(activatedRoute: ActivatedRoute): ActivatedRoute {
        if (activatedRoute.firstChild) {
            return this.getChild(activatedRoute.firstChild);
        } else {
            return activatedRoute;
        }
    }

    showFeedback() {
        this._misc.checkLastFeedback().subscribe(rsp => {
            if (rsp) {
                this._dialog.open(FeedbackComponent, {
                    width: '700px'
                })
            }
        })
    }

    cancelImpersonation() {
        this.processing = true;
        this._user.cancelImpersonation().subscribe(rsp => {
            window.location.reload();
        });
    }

    ngOnDestroy() {
        this.subscription.unsubscribe();
    }
}
